import { useState, useCallback } from 'react';
import { PalletSummaryDto, CreateOrderSSCCRequest } from '../../../../../types/picking';
import { pickingService } from '../../../../../services/pickingService';
import { ExtendedPickingLine } from './usePickingData';

export interface UsePickingSplitReturn {
  // Split modal state
  showSplitModal: boolean;
  splitLineNo: number | null;
  splitQuantities: { [palletId: string]: number };
  setSplitQuantities: React.Dispatch<React.SetStateAction<{ [palletId: string]: number }>>;

  // Distribution modal state
  showQuantityDistribution: boolean;
  distributionLineNo: number | null;
  distributionQuantities: { [palletId: string]: number };
  setDistributionQuantities: React.Dispatch<React.SetStateAction<{ [palletId: string]: number }>>;

  // Handlers
  handleSplitLine: (lineNo: number) => void;
  confirmSplit: () => Promise<void>;
  closeSplitModal: () => void;
  confirmQuantityDistribution: () => Promise<void>;
  closeDistributionModal: () => void;
}

interface UsePickingSplitDeps {
  pListId: number;
  pickingLines: ExtendedPickingLine[];
  setPickingLines: React.Dispatch<React.SetStateAction<ExtendedPickingLine[]>>;
  setActiveLineIndex: React.Dispatch<React.SetStateAction<number | null>>;
  palletSummaries: PalletSummaryDto[];
  lineStartTimes: Map<number, string>;
  loadPallets: () => Promise<void>;
  reorganizeLines: (lines: ExtendedPickingLine[]) => ExtendedPickingLine[];
}

export function usePickingSplit(deps: UsePickingSplitDeps): UsePickingSplitReturn {
  const {
    pListId,
    pickingLines,
    setPickingLines,
    setActiveLineIndex,
    palletSummaries,
    lineStartTimes,
    loadPallets,
    reorganizeLines,
  } = deps;

  // Split state
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitLineNo, setSplitLineNo] = useState<number | null>(null);
  const [splitQuantities, setSplitQuantities] = useState<{ [palletId: string]: number }>({});

  // Distribution state
  const [showQuantityDistribution, setShowQuantityDistribution] = useState(false);
  const [distributionLineNo, setDistributionLineNo] = useState<number | null>(null);
  const [distributionQuantities, setDistributionQuantities] = useState<{ [palletId: string]: number }>({});

  // ─────────────────────────────────────────────────
  // Split
  // ─────────────────────────────────────────────────

  const handleSplitLine = useCallback((lineNo: number) => {
    setSplitLineNo(lineNo);
    const initialSplit: { [palletId: string]: number } = {};
    palletSummaries.forEach(pallet => {
      initialSplit[pallet.sscc] = 0;
    });
    setSplitQuantities(initialSplit);
    setShowSplitModal(true);
  }, [palletSummaries]);

  const confirmSplit = useCallback(async () => {
    if (!splitLineNo) return;

    const line = pickingLines.find(l => l.pListLineNo === splitLineNo);
    if (!line) return;

    const totalSplit = Object.values(splitQuantities).reduce((sum, qty) => sum + qty, 0);
    if (totalSplit > line.qty) {
      alert('Total kan ikke overstige bestilt antall!');
      return;
    }

    try {
      // STEP 1: Delete all existing OrderSSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === splitLineNo);

      console.log(`🗑️ Deleting ${lineSSCCs.length} existing OrderSSCC records for line ${splitLineNo}`);
      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }

      // STEP 2: Create new OrderSSCC records for each pallet allocation
      console.log('📦 Creating new split allocations:', splitQuantities);
      for (const [sscc, qty] of Object.entries(splitQuantities)) {
        if (qty > 0) {
          const ssccRequest: CreateOrderSSCCRequest = {
            companyNo: '',
            pListID: pListId,
            orderNo: line.orderNo,
            lineNo: splitLineNo,
            sscc: sscc,
            gtin: line.itemNo,
            quantity: qty,
            ssccLevel: 1,
            itemBatchNo: 'SPLIT_' + Date.now(),
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0,
            locationName: line.location
          };
          await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
        }
      }

      // STEP 3: Update picked quantity in PListLine
      const startDateTime = lineStartTimes.get(splitLineNo);
      await pickingService.updatePickedQuantity(pListId, splitLineNo, totalSplit, undefined, startDateTime);

      // STEP 4: Reload pallets from database
      await loadPallets();

      // Update local state
      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === splitLineNo
          ? {
            ...l,
            qtyPicked: totalSplit,
            isCompleted: totalSplit > 0,
            isPartiallyPicked: totalSplit > 0 && totalSplit < l.qty,
            isFullyPicked: totalSplit >= l.qty,
            palletId: Object.keys(splitQuantities).find(sscc => splitQuantities[sscc] > 0)
          }
          : l
      );

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);

      setShowSplitModal(false);
      setSplitLineNo(null);
      setSplitQuantities({});
    } catch (error) {
      console.error('Error splitting line:', error);
      alert('Kunne ikke dele linje. Prøv igjen.');
    }
  }, [pListId, splitLineNo, splitQuantities, pickingLines, lineStartTimes, loadPallets, reorganizeLines, setPickingLines, setActiveLineIndex]);

  const closeSplitModal = useCallback(() => {
    setShowSplitModal(false);
    setSplitLineNo(null);
    setSplitQuantities({});
  }, []);

  // ─────────────────────────────────────────────────
  // Distribution
  // ─────────────────────────────────────────────────

  const confirmQuantityDistribution = useCallback(async () => {
    if (!distributionLineNo) return;

    const line = pickingLines.find(l => l.pListLineNo === distributionLineNo);
    if (!line) return;

    const totalDistributed = Object.values(distributionQuantities).reduce((sum, qty) => sum + qty, 0);

    if (totalDistributed > line.qty) {
      alert('Total kan ikke overstige bestilt antall!');
      return;
    }

    try {
      // STEP 1: Delete all existing OrderSSCC records for this line
      const allSSCCs = await pickingService.getOrderSSCCs(pListId);
      const lineSSCCs = allSSCCs.filter(sscc => sscc.lineNo === distributionLineNo);

      console.log(`🗑️ Deleting ${lineSSCCs.length} existing OrderSSCC records for line ${distributionLineNo}`);
      for (const sscc of lineSSCCs) {
        await pickingService.deleteSSCC(pListId, sscc.id);
      }

      // STEP 2: Create new OrderSSCC records for each pallet allocation
      console.log('📦 Creating new distribution allocations:', distributionQuantities);
      for (const [sscc, qty] of Object.entries(distributionQuantities)) {
        if (qty > 0) {
          const ssccRequest: CreateOrderSSCCRequest = {
            companyNo: '',
            pListID: pListId,
            orderNo: line.orderNo,
            lineNo: distributionLineNo,
            sscc: sscc,
            gtin: line.itemNo,
            quantity: qty,
            ssccLevel: 1,
            itemBatchNo: 'DIST_' + Date.now(),
            itemBatchExpDate: null,
            stackingWeight: 0,
            grossWeight: 0,
            locationName: line.location
          };
          await pickingService.createOrUpdateSSCC(pListId, ssccRequest);
        }
      }

      // STEP 3: Update picked quantity in PListLine
      const startDateTime = lineStartTimes.get(distributionLineNo);
      await pickingService.updatePickedQuantity(pListId, distributionLineNo, totalDistributed, undefined, startDateTime);

      // STEP 4: Reload pallets from database
      await loadPallets();

      const isFullyPicked = totalDistributed >= line.qty;
      const isPartiallyPicked = totalDistributed > 0 && totalDistributed < line.qty;

      const updatedLines = pickingLines.map(l =>
        l.pListLineNo === distributionLineNo
          ? {
            ...l,
            qtyPicked: totalDistributed,
            isCompleted: totalDistributed > 0,
            isPartiallyPicked,
            isFullyPicked,
            palletId: Object.keys(distributionQuantities).find(sscc => distributionQuantities[sscc] > 0)
          }
          : l
      );

      const reorganized = reorganizeLines(updatedLines);
      setPickingLines(reorganized);
      setActiveLineIndex(0);

      setShowQuantityDistribution(false);
      setDistributionLineNo(null);
      setDistributionQuantities({});
    } catch (error) {
      console.error('Error distributing quantity:', error);
      alert('Kunne ikke distribuere antall. Prøv igjen.');
    }
  }, [pListId, distributionLineNo, distributionQuantities, pickingLines, lineStartTimes, loadPallets, reorganizeLines, setPickingLines, setActiveLineIndex]);

  const closeDistributionModal = useCallback(() => {
    setShowQuantityDistribution(false);
    setDistributionLineNo(null);
    setDistributionQuantities({});
  }, []);

  return {
    // Split modal state
    showSplitModal,
    splitLineNo,
    splitQuantities,
    setSplitQuantities,

    // Distribution modal state
    showQuantityDistribution,
    distributionLineNo,
    distributionQuantities,
    setDistributionQuantities,

    // Handlers
    handleSplitLine,
    confirmSplit,
    closeSplitModal,
    confirmQuantityDistribution,
    closeDistributionModal,
  };
}
